import { apiFetch } from './ApiService.js';
import type { IToastService } from './ToastService.js';
import type { IModalService } from './ModalService.js';
import type { IModalConfirmHelper } from './ModalConfirmHelper.js';

/// Håndterer write-actions for domæner (`data-action="create-domain"`).
/// Læser felter fra modal-DOM, kalder Admin-app's `/api/domains`-broker som
/// in-proc-vejen til `DomainManagementService.CreateAsync`.
///
/// Per FR-ADM-09: DKIM-generering er en SEPARAT handling fra domæne-detail-
/// siden, ikke en del af add-domain-flow'et.

interface CreateDomainPayload {
  name: string;
}

interface BrokerResponse {
  ok: boolean;
  detail?: string | null;
}

export interface IDomainActionsService {
  createFromModal(modalId: string): Promise<void>;
  verifyDns(domain: string): Promise<void>;
  deleteWithConfirm(name: string): void;
  issueDkim(name: string): Promise<void>;
  rotateDkim(name: string, selector: string): Promise<void>;
  deleteDkim(name: string, selector: string): Promise<void>;
  toggleDkimKey(selector: string): void;
}

type DnsStatusKind = 'Ok' | 'MissingOnServer' | 'MissingInDns';

interface DnsVerificationDetail {
  recordType: string;
  passed: boolean;
  message: string;
}

interface DnsRecordStatus {
  kind: DnsStatusKind;
  expectedExists: boolean;
  verifiedInDns: boolean;
  details: DnsVerificationDetail[];
}

interface DnsHealth {
  mx: DnsRecordStatus;
  spf: DnsRecordStatus;
  dkim: DnsRecordStatus;
  dmarc: DnsRecordStatus;
}

interface VerifyDnsResponse {
  ok: boolean;
  health?: DnsHealth;
  detail?: string;
}

export class DomainActionsService implements IDomainActionsService {
  /// RFC 1035-tolerant subset: lille bogstaver + tal + bindestreg, mindst
  /// ét punktum, TLD min. 2 bogstaver. Server forbliver authoritative; det
  /// her er kun øjeblikkelig UX-feedback.
  private static readonly NAME_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,}$/;

  constructor(
    private readonly toast: IToastService,
    private readonly modal: IModalService,
    private readonly confirmer: IModalConfirmHelper,
    private readonly doc: Document = document,
  ) { }

  /// Hard-delete af domæne med GitHub-style confirm-modal. På success →
  /// redirect til /Domains (vi er på Detail-siden der ophører).
  deleteWithConfirm(name: string): void {
    this.confirmer.open({
      title: 'Slet domæne endeligt',
      message: `Sletter domænet ${name}. Domænet skal være tomt (ingen brugere) — ellers fejler kaldet med 409.`,
      expectedName: name,
      actionUrl: `/api/domains/${encodeURIComponent(name)}/delete`,
      successMsg: `Domæne slettet: ${name}`,
      redirectTo: '/Domains',
    });
  }

  /// Generér ny DKIM-key. Ingen confirm — det er en additiv handling der ikke
  /// rører eksisterende keys.
  async issueDkim(name: string): Promise<void> {
    await this.postSimple(
      `/api/domains/${encodeURIComponent(name)}/dkim/issue`,
      `Ny DKIM-key genereret for ${name}`,
      'Kunne ikke generere DKIM-key',
    );
  }

  /// Rotér eksisterende selector. Browser-confirm — handling er reversibel
  /// (gammel selector går i 'rotating'-status, ny i 'active'; gammel kan
  /// senere retires manuelt).
  async rotateDkim(name: string, selector: string): Promise<void> {
    const ok = window.confirm(
      `Rotér DKIM-selector ${selector}?\n\n` +
      `Gammel key markeres som 'rotating' (signer stadig under DNS-cache-TTL grace); ` +
      `en ny 'active' key genereres. Begge nye records skal lægges i DNS.`,
    );
    if (!ok) return;
    await this.postSimple(
      `/api/domains/${encodeURIComponent(name)}/dkim/${encodeURIComponent(selector)}/rotate`,
      `Selector roteret: ${selector}`,
      'Kunne ikke rotere DKIM-key',
    );
  }

  /// Tilbagekald selector. Browser-confirm. Server afviser med 409 hvis det
  /// er den eneste aktive key for domænet (FR-ADM-29).
  async deleteDkim(name: string, selector: string): Promise<void> {
    const ok = window.confirm(
      `Tilbagekald DKIM-selector ${selector}?\n\n` +
      `Fjerner selector permanent. Hvis det er den sidste aktive key for ` +
      `domænet afvises kaldet for at undgå unsigned outbound mail.`,
    );
    if (!ok) return;
    await this.postSimple(
      `/api/domains/${encodeURIComponent(name)}/dkim/${encodeURIComponent(selector)}/delete`,
      `Selector tilbagekaldt: ${selector}`,
      'Kunne ikke tilbagekalde DKIM-key',
    );
  }

  /// Toggle reveal af public-key-blob på DKIM-key-card. Pure DOM, ingen API.
  toggleDkimKey(selector: string): void {
    const target = this.doc.querySelector<HTMLElement>(`[data-dkim-key="${selector}"]`);
    if (!target) {
      console.warn(`toggle-dkim-key: ingen reveal-block fundet for selector ${selector}`);
      return;
    }
    target.style.display = target.style.display === 'none' ? 'block' : 'none';
  }

  /// Shared fetch-pattern for fire-and-forget POST-actions (issue/rotate/delete-dkim).
  /// Toast på success/fail og siden-reload på success.
  private async postSimple(url: string, successMsg: string, errPrefix: string): Promise<void> {
    try {
      const resp = await apiFetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
      });
      const body = await this.readBody(resp);
      if (resp.ok && body.ok) {
        this.toast.show(successMsg, 'success');
        window.location.reload();
        return;
      }
      const msg = body.detail ?? `HTTP ${resp.status}`;
      this.toast.show(`${errPrefix}: ${msg}`, 'error');
    } catch (e) {
      console.error(e);
      this.toast.show('Netværksfejl — kunne ikke nå Admin-server', 'error');
    }
  }

  async createFromModal(modalId: string): Promise<void> {
    const modal = this.doc.getElementById(modalId);
    if (!modal) {
      console.warn(`DomainActionsService: modal '${modalId}' findes ikke`);
      return;
    }

    const payload = this.readPayload(modal);
    if (!this.validate(modal, payload)) return;

    try {
      const resp = await apiFetch('/api/domains', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload),
      });

      const body = await this.readBody(resp);
      if (resp.ok && body.ok) {
        this.toast.show(`Domæne oprettet: ${payload.name}`, 'success');
        this.modal.close(modalId);
        window.location.reload();
        return;
      }

      const msg = body.detail ?? `HTTP ${resp.status}`;
      this.toast.show(`Kunne ikke oprette domæne: ${msg}`, 'error');
    } catch (e) {
      console.error(e);
      this.toast.show('Netværksfejl — kunne ikke nå Admin-server', 'error');
    }
  }

  /// POST /api/domains/{name}/verify-dns → live DNS-probe + sammenligning.
  /// Opdaterer DNS-records-rækkernes status-tag på Detail-siden + viser toast
  /// med summary. Per-record-type-granularitet (alle MX-rows får samme tone,
  /// alle DKIM-rows samme osv.); per-selector-fejl vises i toast-message.
  async verifyDns(domain: string): Promise<void> {
    try {
      const resp = await apiFetch(`/api/domains/${encodeURIComponent(domain)}/verify-dns`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
      });

      const body = await this.readVerifyBody(resp);
      if (!resp.ok || !body.ok || !body.health) {
        this.toast.show(body.detail ?? `DNS-tjek fejlede (HTTP ${resp.status})`, 'error');
        return;
      }

      this.applyHealthToDom(body.health);
      this.toastSummary(body.health);
    } catch (e) {
      console.error(e);
      this.toast.show('Netværksfejl — kunne ikke køre DNS-tjek', 'error');
    }
  }

  private applyHealthToDom(health: DnsHealth): void {
    const buckets: Array<[string, DnsRecordStatus]> = [
      ['mx', health.mx], ['spf', health.spf], ['dkim', health.dkim], ['dmarc', health.dmarc],
    ];
    for (const [kind, status] of buckets) {
      const tone = this.toneClass(status);
      const icon = this.toneIcon(status);
      this.doc.querySelectorAll<HTMLElement>(`[data-dns-record-type="${kind}"]`).forEach(row => {
        const tag = row.querySelector<HTMLElement>('[data-dns-status-tag]');
        if (!tag) return;
        tag.classList.remove('tag-ok', 'tag-warn', 'tag-err', 'tag-muted');
        tag.classList.add(tone);
        tag.innerHTML = `<i class="ph ${icon}"></i>`;
      });
    }
  }

  private toneClass(s: DnsRecordStatus): string {
    if (s.kind === 'Ok') return 'tag-ok';
    if (s.kind === 'MissingOnServer') return 'tag-muted';
    return 'tag-err'; // MissingInDns
  }

  private toneIcon(s: DnsRecordStatus): string {
    if (s.kind === 'Ok') return 'ph-check-circle';
    if (s.kind === 'MissingOnServer') return 'ph-minus-circle';
    return 'ph-x-circle';
  }

  private toastSummary(health: DnsHealth): void {
    const failed: string[] = [];
    const allDetails = [
      ...health.mx.details, ...health.spf.details,
      ...health.dkim.details, ...health.dmarc.details,
    ];
    for (const d of allDetails) {
      if (!d.passed) failed.push(`${d.recordType.toUpperCase()}: ${d.message}`);
    }

    if (failed.length === 0) {
      this.toast.show('DNS-konfiguration verificeret — alle records OK', 'success');
    } else {
      // Vi viser første fejl i toast'en; admin kan se detalje pr-record-row.
      this.toast.show(`${failed.length} DNS-fejl — fx ${failed[0]}`, 'error');
    }
  }

  private async readVerifyBody(resp: Response): Promise<VerifyDnsResponse> {
    try {
      return await resp.json() as VerifyDnsResponse;
    } catch {
      return { ok: false, detail: `HTTP ${resp.status}` };
    }
  }

  private readPayload(modal: HTMLElement): CreateDomainPayload {
    // Server lowercaser + trimmer alligevel, men gør det client-side så
    // UX-feedback (regex) ser samme normaliserede værdi som server.
    return {
      name: this.readInput(modal, 'name').toLowerCase(),
    };
  }

  private readInput(scope: HTMLElement, name: string): string {
    const el = scope.querySelector<HTMLInputElement>(`[name="${name}"]`);
    return (el?.value ?? '').trim();
  }

  private validate(modal: HTMLElement, p: CreateDomainPayload): boolean {
    this.clearErrors(modal);
    if (!p.name) {
      this.showFieldError(modal, 'name', 'Domænenavn er påkrævet');
      return false;
    }
    if (!DomainActionsService.NAME_REGEX.test(p.name)) {
      this.showFieldError(modal, 'name', 'Ugyldigt domænenavn-format (fx example.com)');
      return false;
    }
    return true;
  }

  private clearErrors(modal: HTMLElement): void {
    modal.querySelectorAll<HTMLElement>('[data-field-error]').forEach(el => {
      el.textContent = '';
    });
  }

  private showFieldError(modal: HTMLElement, field: string, msg: string): void {
    const el = modal.querySelector<HTMLElement>(`[data-field-error="${field}"]`);
    if (el) el.textContent = msg;
  }

  private async readBody(resp: Response): Promise<BrokerResponse> {
    try {
      return await resp.json() as BrokerResponse;
    } catch {
      return { ok: false, detail: `HTTP ${resp.status}` };
    }
  }
}
